import { useRef, type KeyboardEvent, type ReactElement } from "react";
import { classes } from "./classes.ts";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";
import { buttonFrame, choiceForKey, frameClass, nextEnabled } from "./state.ts";

/**
 * A segmented choice (brief section 5.1): one of a few options as joined buttons, a radio group to
 * assistive technology. A click or a tap picks an option; with the keyboard, Tab reaches the group once and
 * the arrow keys, Home and End move the choice, skipping disabled options and wrapping. A key the group
 * answers is prevented, so the input router and Phaser, which both ignore a prevented key, never move the
 * character with it.
 */

/**
 * One option of a segmented choice.
 */
export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
}

/**
 * One option as a radio button.
 *
 * @param props The option's label, whether it is checked, disabled or the group's tab stop, a ref setter
 *   for moving focus to it, and `onPick`.
 * @returns The option.
 */
function Option({ label, checked, disabled, tabStop, refer, onPick }: { readonly label: string; readonly checked: boolean; readonly disabled: boolean; readonly tabStop: boolean; readonly refer: (element: HTMLButtonElement | null) => void; readonly onPick: () => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const frame = buttonFrame("secondary", { ...state, disabled, selected: checked });
  return (
    <button
      ref={refer}
      type="button"
      role="radio"
      aria-checked={checked}
      tabIndex={tabStop ? 0 : -1}
      disabled={disabled}
      className={classes("ot-button", "ot-button--secondary", frameClass(frame), state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", disabled && "is-disabled", checked && "is-selected")}
      data-kit="segment"
      onClick={onPick}
      style={{ height: logical(KIT_METRICS.button.height), paddingLeft: logical(KIT_METRICS.button.paddingX), paddingRight: logical(KIT_METRICS.button.paddingX) }}
      {...handlers}
    >
      <span className="ot-button__label">{label}</span>
    </button>
  );
}

/**
 * A segmented choice.
 *
 * @param props `describedBy`, the id of an element that describes the group, when it has one; the group's
 *   accessible `label`, its `options`, the chosen `value` (or null for none) and
 *   `onChange` with the value picked.
 * @returns The radio group.
 */
export function Segmented<T extends string>({ label, options, value, onChange, describedBy }: { readonly label: string; readonly options: readonly SegmentedOption<T>[]; readonly value: T | null; readonly onChange: (value: T) => void; readonly describedBy?: string | undefined }): ReactElement {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const disabled = options.map((option) => option.disabled === true);
  const chosen = options.findIndex((option) => option.value === value);
  const tabStop = chosen >= 0 && disabled[chosen] === false ? chosen : nextEnabled(disabled, -1, 1);

  /**
   * Moves the choice with the arrow keys, Home and End, and focuses the option chosen.
   *
   * @param event The key press on an option.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const from = buttons.current.findIndex((button) => button === event.target);
    const to = choiceForKey(event.key, disabled, from);
    if (to === null || to < 0) {
      return;
    }
    event.preventDefault();
    const option = options[to] as SegmentedOption<T>;
    onChange(option.value);
    buttons.current[to]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} aria-describedby={describedBy} className="ot-segmented" data-kit="segmented" style={{ gap: logical(KIT_METRICS.segmented.gap) }} onKeyDown={onKeyDown}>
      {options.map((option, index) => (
        <Option
          key={option.value}
          label={option.label}
          checked={index === chosen}
          disabled={option.disabled === true}
          tabStop={index === tabStop}
          refer={(element) => {
            buttons.current[index] = element;
          }}
          onPick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}
